import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import type { TaskProcedureBinding } from '@erp/shared-types';
import { orpc } from 'src/api/rpc/client';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import paths from 'src/paths';

/** Preview availability using only execution-relevant bindings, so typing notes does not refetch. */
export function ProcedureReadinessPreview({
  agentId,
  procedures,
}: {
  agentId: string;
  procedures: TaskProcedureBinding[];
}) {
  const { data, isPending, isError } = useQuery({
    ...orpc.taskTypes.previewProcedureReadiness.queryOptions({
      input: {
        agentDefinitionId: agentId,
        procedures: procedures.map(
          ({ sopId, mode, phase, pinnedVersionId }) => ({
            sopId,
            mode,
            phase,
            pinnedVersionId,
          }),
        ),
      },
    }),
    staleTime: 0,
  });
  return (
    <AlertContainer>
      <AlertDescription>
        {isPending ? (
          <T context="procedure readiness loading">
            Checking procedure availability…
          </T>
        ) : isError ? (
          <T context="procedure readiness error">
            Availability could not be checked. Retry before running tasks.
          </T>
        ) : (
          data && (
            <div className="flex flex-col gap-2">
              <p>
                <T context="procedure readiness assigned agent">
                  Assigned agent: <Var>{data.agentName}</Var>
                </T>
              </p>
              {!data.enabled && (
                <p>
                  <T context="procedure readiness disabled agent">
                    Procedures are disabled on this agent.
                  </T>
                </p>
              )}
              {data.issues.length === 0 ? (
                <p>
                  <T context="procedure readiness success">
                    Required procedures are available. They run at the
                    configured point during task execution.
                  </T>
                </p>
              ) : (
                <>
                  <p>
                    <T context="procedure readiness blocker">
                      These requirements block task execution until
                      configuration is corrected:
                    </T>
                  </p>
                  <ul>
                    {data.issues.map((issue) => (
                      <li key={issue.sopId}>
                        <Var>{issue.title}</Var>
                        {' — '}
                        {issue.code === 'not_allowed' ? (
                          <T context="procedure readiness issue">
                            Not available to this agent
                          </T>
                        ) : (
                          <T context="procedure readiness issue">
                            Procedure or version unavailable
                          </T>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <Link
                to={paths.authenticated.backgroundAgents.definitionProcedures}
                params={{ agentDefinitionId: agentId }}
                className="underline"
              >
                <T context="procedure readiness navigation">
                  Review Agent Settings
                </T>
              </Link>
            </div>
          )
        )}
      </AlertDescription>
    </AlertContainer>
  );
}
